import axios from "axios";
import {
  ApiRequest,
  ApiRequestNoLoder,
  ApiDownloadRequest,
  getHost,
  getUserId,
} from "../../services/api";
import { buildQueryString } from "../../utilities/queryParams";

const createAdminHeaders = () => {
  const token = localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    "X-User-Id": getUserId(),
    Authorization: token ? `Bearer ${token}` : "",
  };
};

const SEARCH_CACHE_TTL_MS = 5 * 60 * 1000;
const searchResponseCache = new Map();
const searchInFlightRequests = new Map();

const normalizeSearchTerm = (search = "") => String(search).trim().toLowerCase();

const getCachedSearchResponse = (cacheKey) => {
  const cached = searchResponseCache.get(cacheKey);
  if (!cached) {
    return null;
  }

  if (cached.expiresAt <= Date.now()) {
    searchResponseCache.delete(cacheKey);
    return null;
  }

  return cached.data;
};

const setCachedSearchResponse = (cacheKey, data) => {
  searchResponseCache.set(cacheKey, {
    data,
    expiresAt: Date.now() + SEARCH_CACHE_TTL_MS,
  });
};

const getApiErrorMessage = (error) => {
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }

  const detail = error?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === "object" && item !== null) {
          const prefix = item.field ? `${item.field}: ` : "";
          return `${prefix}${item.message || JSON.stringify(item)}`;
        }
        return String(item);
      })
      .join(", ");
  }

  if (detail && typeof detail === "object") {
    return JSON.stringify(detail);
  }

  if (detail) {
    return String(detail);
  }

  if (error?.message) {
    return error.message;
  }

  return "Something went wrong";
};

const createApiError = (error) => {
  const apiError = new Error(getApiErrorMessage(error));
  apiError.status = error?.response?.status || 0;
  apiError.data = error?.response?.data || null;
  return apiError;
};

const fetchAdminGet = async (path) => {
  try {
    const response = await axios.get(`${getHost()}${path}`, {
      headers: createAdminHeaders(),
    });
    return response.data;
  } catch (error) {
    throw createApiError(error);
  }
};

const pickAllowedFilters = (filters = {}, allowedKeys = []) =>
  allowedKeys.reduce((accumulator, key) => {
    if (Object.prototype.hasOwnProperty.call(filters, key)) {
      accumulator[key] = filters[key];
    }
    return accumulator;
  }, {});

const MATCH_PUBLISH_SUMMARY_FILTER_KEYS = [
  "candidate_id",
  "search",
  "compatibility_level",
  "source_service",
  "decision",
  "is_disliked",
  "match_score_min",
  "match_score_max",
  "published_from",
  "published_to",
  "group_by",
];

const MATCH_PUBLISH_HISTORY_FILTER_KEYS = [
  "candidate_id",
  "search",
  "compatibility_level",
  "source_service",
  "decision",
  "is_disliked",
  "published",
  "match_score_min",
  "match_score_max",
  "published_from",
  "published_to",
  "created_from",
  "created_to",
  "sort_by",
  "sort_order",
  "limit",
  "offset",
];

const MATCHMAKING_RESULTS_FEED_FILTER_KEYS = [
  "uid",
  "user_id",
  "candidate_id",
  "today",
  "published_from",
  "published_to",
  "new",
  "distance",
  "limit",
  "offset",
];

const MEMBERS_OVERVIEW_FILTER_KEYS = [
  "search",
  "premium_filter",
  "assigned_to",
  "gender",
  "city",
  "state",
  "created_from",
  "created_to",
  "last_match_published_from",
  "last_match_published_to",
  "stale_match_published",
  "stale_instructions",
  "liked_not_called_48h",
  "sort_by",
  "sort_order",
  "limit",
  "offset",
];

const USERS_WITHOUT_MATCHES_TODAY_FILTER_KEYS = [
  "search",
  "assigned_to",
  "gender",
  "city",
  "state",
  "date",
  "sort_by",
  "sort_order",
  "limit",
  "offset",
];

export const GetProfileDetails = async (userId) => {
  return await ApiRequest("GET", "admin/v2/profile-details?userId=" + userId);
};
export const GetUserMatches = async (
  userId,
  limit = 10,
  offset = 0,
  filters = {},
) => {
  const {
    filter_type = "all",
    match_status = "all",
    compatibility_level = "all",
    filter_dislikes = true,
  } = filters;
  let url = `admin/v2/user-matches?userId=${userId}&limit=${limit}&offset=${offset}`;
  if (filter_type !== "all") url += `&filter_type=${filter_type}`;
  if (match_status !== "all") url += `&match_status=${match_status}`;
  if (compatibility_level !== "all")
    url += `&compatibility_level=${compatibility_level}`;
  if (filter_dislikes !== undefined && filter_dislikes !== null) {
    url += `&filter_dislikes=${filter_dislikes}`;
  }
  return await ApiRequest("GET", url);
};

export const GetUserSpecialPreferenceActivity = async (
  userId,
  limit = 1000,
  offset = 0,
) => {
  return await ApiRequest(
    "GET",
    `admin/v2/user-special-preference-activity?userId=${encodeURIComponent(userId)}&limit=${limit}&offset=${offset}`,
  );
};

export const GetUserHistory = async (userId) => {
  return await ApiRequest("GET", `admin/v2/user-history?userId=${userId}`);
};

export const getMatchPublishSummary = async (userId, filters = {}) => {
  const queryString = buildQueryString(
    pickAllowedFilters(filters, MATCH_PUBLISH_SUMMARY_FILTER_KEYS),
  );
  const suffix = queryString ? `?${queryString}` : "";

  try {
    const response = await axios.get(
      `${getHost()}admin/v2/users/${encodeURIComponent(userId)}/match-publish-summary${suffix}`,
      { headers: createAdminHeaders() },
    );
    return response.data;
  } catch (error) {
    throw createApiError(error);
  }
};

export const getMatchPublishHistory = async (userId, filters = {}) => {
  const queryString = buildQueryString(
    pickAllowedFilters(filters, MATCH_PUBLISH_HISTORY_FILTER_KEYS),
  );
  const suffix = queryString ? `?${queryString}` : "";

  try {
    const response = await axios.get(
      `${getHost()}admin/v2/users/${encodeURIComponent(userId)}/match-publish-history${suffix}`,
      { headers: createAdminHeaders() },
    );
    return response.data;
  } catch (error) {
    throw createApiError(error);
  }
};

export const getMatchmakingResultsFeed = async (filters = {}) => {
  const queryString = buildQueryString(
    pickAllowedFilters(filters, MATCHMAKING_RESULTS_FEED_FILTER_KEYS),
  );
  const suffix = queryString ? `?${queryString}` : "";

  try {
    const response = await axios.get(
      `${getHost()}admin/v2/matchmaking-results-feed${suffix}`,
      { headers: createAdminHeaders() },
    );
    return response.data;
  } catch (error) {
    throw createApiError(error);
  }
};

export const getMembersOverview = async (filters = {}) => {
  const queryString = buildQueryString(
    pickAllowedFilters(filters, MEMBERS_OVERVIEW_FILTER_KEYS),
  );
  const suffix = queryString ? `?${queryString}` : "";

  try {
    const response = await axios.get(
      `${getHost()}admin/v2/members-overview${suffix}`,
      { headers: createAdminHeaders() },
    );
    return response.data;
  } catch (error) {
    throw createApiError(error);
  }
};

export const getUsersWithoutMatchesToday = async (filters = {}) => {
  const queryString = buildQueryString(
    pickAllowedFilters(filters, USERS_WITHOUT_MATCHES_TODAY_FILTER_KEYS),
  );
  const suffix = queryString ? `?${queryString}` : "";

  try {
    const response = await axios.get(
      `${getHost()}admin/v2/users-without-matches-today${suffix}`,
      { headers: createAdminHeaders() },
    );
    return response.data;
  } catch (error) {
    throw createApiError(error);
  }
};

export const getTeamMemberActivity = async (filters = {}) => {
  const queryString = buildQueryString(
    pickAllowedFilters(filters, [
      "assigned_to",
      "user_search",
      "start_date",
      "end_date",
      "event_type",
      "limit",
      "offset",
      "include_metadata",
    ]),
  );
  const suffix = queryString ? `?${queryString}` : "";

  try {
    const response = await axios.get(
      `${getHost()}admin/v2/team-member-activity${suffix}`,
      { headers: createAdminHeaders() },
    );
    return response.data;
  } catch (error) {
    throw createApiError(error);
  }
};

export const GetUserLikedMatches = async (userId, limit = 10, offset = 0) => {
  return await ApiRequest(
    "GET",
    `admin/v2/user-liked-matches?userId=${userId}&limit=${limit}&offset=${offset}`,
  );
};

export const GetProfileLikedBy = async (userId, limit = 100, offset = 0) => {
  return await ApiRequest(
    "GET",
    `admin/v2/profile-liked-by?userId=${userId}&limit=${limit}&offset=${offset}`,
  );
};

export const GetMarketingStats = async (
  period = "all",
  limit = 100,
  offset = 0,
  filters = {},
) => {
  const params = new URLSearchParams({
    period,
    limit: String(limit),
    offset: String(offset),
  });

  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      params.append(key, String(value).trim());
    }
  });

  let url = `admin/v2/marketing-stats?${params.toString()}`;
  return await ApiRequest("GET", url);
};

export const GetAnalyticsOverview = async (period = "30d") => {
  return await fetchAdminGet(`analytics/overview?period=${period}`);
};

export const GetAnalyticsDailyUsers = async (period = "30d") => {
  return await fetchAdminGet(`analytics/daily-users?period=${period}`);
};

export const GetAnalyticsTopPages = async (period = "30d") => {
  return await fetchAdminGet(`analytics/top-pages?period=${period}`);
};

export const GetAnalyticsTrafficSources = async (period = "30d") => {
  return await fetchAdminGet(`analytics/traffic-sources?period=${period}`);
};

export const GetAnalyticsDevices = async (period = "30d") => {
  return await fetchAdminGet(`analytics/devices?period=${period}`);
};

export const GetAnalyticsCountries = async (period = "30d") => {
  return await fetchAdminGet(`analytics/countries?period=${period}`);
};

export const GetAnalyticsEvents = async (period = "30d") => {
  return await fetchAdminGet(`analytics/events?period=${period}`);
};

export const GetAnalyticsBrowsers = async (period = "30d") => {
  return await fetchAdminGet(`analytics/browsers?period=${period}`);
};

export const GetAnalyticsOS = async (period = "30d") => {
  return await fetchAdminGet(`analytics/os?period=${period}`);
};

export const GetAnalyticsLandingPages = async (period = "30d") => {
  return await fetchAdminGet(`analytics/landing-pages?period=${period}`);
};
export const SearchUsers = async (searchTerm, limit = 50, offset = 0, assignedTo = "") => {
  const assignedFilter = assignedTo
    ? `&assigned_to=${encodeURIComponent(assignedTo)}`
    : "";
  return await ApiRequest(
    "GET",
    `admin/search-users?search=${encodeURIComponent(searchTerm)}&limit=${limit}&offset=${offset}${assignedFilter}`,
  );
};

export const GetTeamMemberAssignedUsers = async (assignedTo, limit = 20, offset = 0) => {
  return await ApiRequest(
    "GET",
    `admin/v2/team-member-assigned-users?assigned_to=${encodeURIComponent(assignedTo)}&limit=${limit}&offset=${offset}`,
  );
};

export const GetBiodataNonUserExportStatus = async (jobId) => {
  return await ApiRequest("GET", `admin/v2/biodata-non-user-export/${jobId}`);
};

export const DownloadBiodataNonUserExport = async (jobId) => {
  return await ApiDownloadRequest(`admin/v2/biodata-non-user-export/${jobId}/download`);
};
export const GetMatchedProfile = async (userId, candidateId) => {
  return await ApiRequest(
    "GET",
    `admin/v2/matched-profile?user_id=${userId}&candidate_id=${candidateId}`,
  );
};
export const GetMatchPairComparison = async (userId, candidateId) => {
  return await ApiRequest(
    "GET",
    `admin/v2/match-pair-comparison?user_id=${encodeURIComponent(userId)}&candidate_id=${encodeURIComponent(candidateId)}`,
  );
};
export const GetDataChecks = async () => {
  return await ApiRequest("GET", "admin/data-checks");
};
export const RunDataCheck = async (key, { user_id, limit = 100, offset = 0 } = {}) => {
  let url = `admin/data-checks/${encodeURIComponent(key)}?limit=${limit}&offset=${offset}`;
  if (user_id) url += `&user_id=${encodeURIComponent(user_id)}`;
  return await ApiRequest("GET", url);
};
export const GetUserTransactions = async (userId, params = {}) => {
  const { limit = 100, status, transaction_type } = params;
  let url = `admin/v2/users/${userId}/transactions?limit=${limit}`;
  if (status) url += `&status=${status}`;
  if (transaction_type) url += `&transaction_type=${transaction_type}`;
  return await ApiRequest("GET", url);
};
export const GetPassword = async (userId) => {
  return await ApiRequest("GET", "admin/user-password?userId=" + userId);
};
export const GetUsers = async (
  limit = 1000,
  offset = 0,
  profileStatus = null,
  filters = {},
  user_type = null,
) => {
  let url = `admin/users-list?limit=${limit}&offset=${offset}`;
  if (user_type) {
    url += `&user_type=${user_type}`;
  }

  if (profileStatus) {
    url += `&profile_status=${profileStatus}`;
  }
  if (filters.salary) {
    url += `&salary=${encodeURIComponent(filters.salary)}`;
  }
  if (filters.age) {
    url += `&age=${encodeURIComponent(filters.age)}`;
  }
  if (filters.qualification) {
    url += `&qualification=${encodeURIComponent(filters.qualification)}`;
  }
  if (filters.profession) {
    url += `&profession=${encodeURIComponent(filters.profession)}`;
  }
  if (filters.premium) {
    url += `&premium=${filters.premium}`;
  }
  if (filters.city) {
    url += `&city=${encodeURIComponent(filters.city)}`;
  }
  if (filters.state) {
    url += `&state=${encodeURIComponent(filters.state)}`;
  }
  if (filters.community) {
    url += `&community=${encodeURIComponent(filters.community)}`;
  }
  if (filters.gender) {
    url += `&gender=${encodeURIComponent(filters.gender)}`;
  }
  if (filters.manglik) {
    url += `&manglik=${encodeURIComponent(filters.manglik)}`;
  }
  if (filters.marital_status) {
    url += `&marital_status=${encodeURIComponent(filters.marital_status)}`;
  }
  if (filters.family_type) {
    url += `&family_type=${encodeURIComponent(filters.family_type)}`;
  }
  if (filters.family_background) {
    url += `&family_background=${encodeURIComponent(filters.family_background)}`;
  }
  if (filters.assigned_to) {
    url += `&assigned_to=${encodeURIComponent(filters.assigned_to)}`;
  }
  if (filters.min_height) {
    url += `&min_height=${encodeURIComponent(filters.min_height)}`;
  }
  if (filters.max_height) {
    url += `&max_height=${encodeURIComponent(filters.max_height)}`;
  }
  return await ApiRequest("GET", url);
};

export const GetUserPublishedSpecialPreferenceInsight = async (
  userId,
  limit = 500,
) => {
  return await ApiRequest(
    "GET",
    `admin/v2/user-special-preference-published-insight?userId=${encodeURIComponent(userId)}&limit=${limit}`,
  );
};
export const GetGlobalSpecialPreferencePublishedMatches = async ({
  status,
  search = "",
  limit = 50,
  offset = 0,
}) => {
  const params = new URLSearchParams({ status, limit: String(limit), offset: String(offset) });
  if (search.trim()) params.set("search", search.trim());
  return await ApiRequest("GET", `admin/v2/special-preference-published-matches?${params.toString()}`);
};
export const GetSpecialPreferenceUsers = async ({
  search = "",
  activeOnly = true,
  limit = 25,
  offset = 0,
} = {}) => {
  const params = new URLSearchParams({
    active_only: String(activeOnly),
    limit: String(limit),
    offset: String(offset),
  });
  if (search.trim()) params.set("search", search.trim());
  return await ApiRequest(
    "GET",
    `admin/v2/special-preference-users?${params.toString()}`,
  );
};
export const GetPrompts = async () => {
  return await ApiRequest("GET", "get_prompts");
};

export const GetChatGPTPrompts = async () => {
  return await ApiRequest("GET", "admin/v2/chatgpt-prompts");
};

export const GetWhatsappMessages = async (whatsappUserId) => {
  return await ApiRequestNoLoder("GET", "whatsapp/messages/" + whatsappUserId);
};
export const GetUserWhatsappChat = async (userId, limit = 25, offset = 0) => {
  return await ApiRequestNoLoder(
    "GET",
    `admin/v2/users/${encodeURIComponent(userId)}/whatsapp-chat?limit=${limit}&offset=${offset}`,
  );
};
export const GetWhatsappTemplateFieldSources = async () => {
  return await ApiRequestNoLoder("GET", "admin/whatsapp/template-field-sources");
};
export const GetWhatsappTemplateRecipients = async (data) => {
  return await ApiRequestNoLoder("POST", "admin/whatsapp/template-recipients", data);
};
export const GetWhatsappUsers = async () => {
  return await ApiRequestNoLoder("GET", "whatsapp/users");
};
export const GetDashboardUsersByGender = async (community = "") => {
  const suffix = community ? `?community=${encodeURIComponent(community)}` : "";
  return await ApiRequest("GET", `admin/v2/get-users-by-gender${suffix}`);
};

// SUPER_ADMIN: Get all admins
export const GetAllAdmins = async (filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      params.append(key, String(value).trim());
    }
  });
  const suffix = params.toString() ? `?${params.toString()}` : "";
  return await ApiRequest("GET", `admin/list-admins${suffix}`);
};

export const GetAdminLogs = async (skipOrParams = 0, limit = 100, filters = {}) => {
  let skipVal = 0;
  let limitVal = 100;
  let adminNameVal = "";
  let endpointVal = "";
  let statusVal = "";
  let userIdVal = "";
  let candidateIdVal = "";

  if (typeof skipOrParams === "object" && skipOrParams !== null) {
    skipVal = skipOrParams.skip ?? 0;
    limitVal = skipOrParams.limit ?? 100;
    adminNameVal = skipOrParams.admin_name ?? "";
    endpointVal = skipOrParams.endpoint ?? "";
    statusVal = skipOrParams.status ?? "";
    userIdVal = skipOrParams.user_id ?? "";
    candidateIdVal = skipOrParams.candidate_id ?? "";
  } else {
    skipVal = skipOrParams;
    limitVal = limit;
    adminNameVal = filters.admin_name ?? "";
    endpointVal = filters.endpoint ?? "";
    statusVal = filters.status ?? "";
    userIdVal = filters.user_id ?? "";
    candidateIdVal = filters.candidate_id ?? "";
  }

  let url = `admin/show-admin-log?skip=${skipVal}&limit=${limitVal}`;
  if (adminNameVal) url += `&admin_name=${encodeURIComponent(adminNameVal)}`;
  if (endpointVal) url += `&endpoint=${encodeURIComponent(endpointVal)}`;
  if (statusVal) url += `&status=${encodeURIComponent(statusVal)}`;
  if (userIdVal) url += `&user_id=${encodeURIComponent(userIdVal)}`;
  if (candidateIdVal) url += `&candidate_id=${encodeURIComponent(candidateIdVal)}`;

  return await ApiRequest("GET", url);
};

export const GetServiceAddLogs = async (skip = 0, limit = 100, filters = {}) => {
  let url = `admin/show-service-add-logs?skip=${skip}&limit=${limit}`;
  if (filters.admin_name) url += `&admin_name=${encodeURIComponent(filters.admin_name)}`;
  if (filters.status) url += `&status=${encodeURIComponent(filters.status)}`;
  if (filters.service_name) url += `&service_name=${encodeURIComponent(filters.service_name)}`;
  if (filters.start_date) url += `&start_date=${encodeURIComponent(filters.start_date)}`;
  if (filters.end_date) url += `&end_date=${encodeURIComponent(filters.end_date)}`;
  return await ApiRequest("GET", url);
};

export const getExportPublishProfiles = async (published, from, to) => {
  let url = `export-published-profiles?published=${published}`;
  if (from && to) {
    url += `&from=${from}&to=${to}`;
  }
  return await ApiDownloadRequest(url);
};

export const getExportIncompleteProfiles = async () => {
  try {
    const fileName = await ApiDownloadRequest("export-incomplete-profiles");
    return { success: true, fileName };
  } catch (error) {
    // Backend may return 404 if no incomplete profiles found
    if (error?.response?.status === 404) {
      return { success: false, message: "No matches found" };
    }
    return { success: false, error };
  }
};

export const GetTransactions = async () => {
  return await ApiRequestNoLoder("GET", "admin/transactions");
};

export const GetTransactionsV2 = async (params = {}) => {
  const {
    limit = 10,
    offset = 0,
    status,
    transaction_type,
    user_id,
    from_date,
    to_date,
  } = params;
  let url = `admin/v2/transactions?limit=${limit}&offset=${offset}`;
  if (status) url += `&status=${status}`;
  if (transaction_type) url += `&transaction_type=${transaction_type}`;
  if (user_id) url += `&user_id=${user_id}`;
  if (from_date) url += `&from_date=${from_date}`;
  if (to_date) url += `&to_date=${to_date}`;
  return await ApiRequest("GET", url);
};

export const GetWhatsAppBiodataMedia = async (params = {}) => {
  const query = new URLSearchParams();
  query.set("page", String(params.page || 1));
  query.set("page_size", String(params.page_size || 25));
  if (params.start_date) query.set("start_date", params.start_date);
  if (params.end_date) query.set("end_date", params.end_date);
  if (params.sender_name) query.set("sender_name", params.sender_name.trim());
  if (params.candidate_name) {
    query.set("candidate_name", params.candidate_name.trim());
  }
  if (params.is_duplicate !== "" && params.is_duplicate != null) {
    query.set("is_duplicate", String(params.is_duplicate));
  }
  if (params.is_available !== "" && params.is_available != null) {
    query.set("is_available", String(params.is_available));
  }
  if (params.extraction_status) {
    query.set("extraction_status", params.extraction_status);
  }
  if (params.previous_only) query.set("previous_only", "true");
  if (params.auto_created_only) query.set("auto_created_only", "true");
  return await fetchAdminGet(`admin/whatsapp-biodata/media?${query.toString()}`);
};

export const DownloadPreviousWhatsAppBiodataPdf = async (params = {}) => {
  const query = new URLSearchParams();
  if (params.start_date) query.set("start_date", params.start_date);
  if (params.end_date) query.set("end_date", params.end_date);
  if (params.candidate_name) {
    query.set("candidate_name", params.candidate_name.trim());
  }
  const suffix = query.toString() ? `?${query.toString()}` : "";
  try {
    const response = await axios.get(
      `${getHost()}admin/whatsapp-biodata/media/previous/export-pdf${suffix}`,
      {
        headers: createAdminHeaders(),
        responseType: "blob",
      },
    );
    const disposition = response.headers?.["content-disposition"] || "";
    const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
    return {
      blob: response.data,
      filename: filenameMatch?.[1] || "previous-biodata.pdf",
    };
  } catch (error) {
    throw createApiError(error);
  }
};

export const GetWhatsAppBiodataConnection = async () => {
  return await fetchAdminGet(
    `admin/whatsapp-biodata/connection?_=${Date.now()}`,
  );
};

export const GetOnboardingExportGroup = async () => {
  return await fetchAdminGet("admin/whatsapp-biodata/onboarding-export/group");
};

export const GetOnboardingExportRecords = async (params = {}) => {
  const query = new URLSearchParams();
  query.set("page", String(params.page || 1));
  query.set("page_size", String(params.page_size || 25));
  if (params.start_date) query.set("start_date", params.start_date);
  if (params.end_date) query.set("end_date", params.end_date);
  if (params.status) query.set("status", params.status);
  if (params.is_duplicate !== "" && params.is_duplicate != null) query.set("is_duplicate", String(params.is_duplicate));
  if (params.search) query.set("search", params.search.trim());
  return await fetchAdminGet(`admin/whatsapp-biodata/onboarding-export/records?${query.toString()}`);
};

export const DownloadOnboardingBiodataExcel = async (startDate, endDate) => {
  const params = new URLSearchParams();
  if (startDate) params.set("start_date", startDate);
  if (endDate) params.set("end_date", endDate);
  const query = params.toString() ? `?${params.toString()}` : "";
  try {
    const response = await axios.get(
      `${getHost()}admin/whatsapp-biodata/onboarding-export.xlsx${query}`,
      { headers: createAdminHeaders(), responseType: "blob" },
    );
    const disposition = response.headers?.["content-disposition"] || "";
    const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
    return {
      blob: response.data,
      filename: filenameMatch?.[1] || `onboarding_biodata_${startDate}_to_${endDate}.xlsx`,
    };
  } catch (error) {
    throw createApiError(error);
  }
};

export const GetPotentialMatches = async (userId) => {
  return await ApiRequestNoLoder(
    "GET",
    `admin/skipped-match-queue?userId=${userId}`,
  );
};

export const GetPotentialMatchesAdmin = async (
  userId,
  limit = 20,
  offset = 0,
) => {
  return await ApiRequest(
    "GET",
    `admin/potential-matches?user_id=${userId}&limit=${limit}&offset=${offset}`,
  );
};

export const GetUnregisterdUsers = async (params = {}) => {
  const { status, assigned_to, auto_assign, limit, offset } = params;
  const queryParams = new URLSearchParams();

  if (status) queryParams.append("status", status);
  if (assigned_to) queryParams.append("assigned_to", assigned_to);
  if (auto_assign !== undefined)
    queryParams.append("auto_assign", auto_assign.toString());
  if (limit !== undefined) queryParams.append("limit", limit.toString());
  if (offset !== undefined) queryParams.append("offset", offset.toString());

  const queryString = queryParams.toString();
  const url = queryString
    ? `https://api.v2.production.smartmatrimony.ai/admin/unregistered-users?${queryString}`
    : "https://api.v2.production.smartmatrimony.ai/admin/customer-support-users";

  const token = localStorage.getItem("serviceToken");
  console.log("Token being sent:", token);
  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    mode: "cors",
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const GetCommunityStatistics = async () => {
  return await ApiRequest("GET", "admin/v2/community-statistics");
};

export const GetPendingVerifications = async (params = {}) => {
  const queryString = buildQueryString({
    limit: params.limit ?? 10,
    offset: params.offset ?? 0,
    search: params.search,
    city: params.city,
    state: params.state,
    status: params.status,
    score_min: params.score_min,
    score_max: params.score_max,
  });
  const suffix = queryString ? `?${queryString}` : "";

  return await ApiRequest("GET", `admin/pending-verifications${suffix}`);
};

export const GetPublishMultipleService = async (params = {}) => {
  const {
    limit = 10,
    offset = 0,
    status,
    user_id,
    service_name,
    sort_by,
    sort_order,
  } = params;
  let url = `admin/publish/multiple?limit=${limit}&offset=${offset}`;
  if (status) url += `&status=${status}`;
  if (user_id) url += `&user_id=${user_id}`;
  if (service_name) url += `&service_name=${service_name}`;
  if (sort_by) url += `&sort_by=${sort_by}`;
  if (sort_order) url += `&sort_order=${sort_order}`;
  return await ApiRequest("GET", url);
};

export const GetJobDetails = async (jobId) => {
  return await ApiRequest("GET", `admin/jobs/${jobId}`);
};

export const GetInterstedUsers = async () => {
  return await ApiRequest("GET", "admin/get/interested-users");
};

export const GetCustomerSupportUsers = async () => {
  const token = localStorage.getItem("serviceToken");
  const response = await fetch(
    "https://api.v2.production.smartmatrimony.ai/admin/customer-support-users",
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      mode: "cors",
    },
  );

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const GetServerInfo = async () => {
  return await ApiRequest("GET", "admin/server-info");
};

// Attendance APIs
export const GetAttendanceRecords = async () => {
  return await ApiRequest("GET", "employee-attendance/records", null);
};

export const GetExportUsers = async () => {
  return await ApiDownloadRequest("admin/users-list/export-csv");
};

export const GetExportUsersV2 = async (params) => {
  const queryString = new URLSearchParams(params).toString();
  return await ApiDownloadRequest(`admin/v2/users/export?${queryString}`);
};

export const GetInstructionExport = async (params) => {
  const queryString = new URLSearchParams(params).toString();
  return await ApiDownloadRequest(
    `admin/v2/instructions/export?${queryString}`,
  );
};

export const GetCreditPackages = async (isActive = true) => {
  const suffix =
    isActive === undefined || isActive === null || isActive === ""
      ? ""
      : `?is_active=${isActive}`;
  return await ApiRequestNoLoder("GET", `admin/credit-packages${suffix}`);
};

export const GetPreferenceCategories = async () => {
  return await ApiRequest("GET", "profile/get-preference-Categories");
};

export const GetUsersWithInstructions = async (
  limit = 100,
  offset = 0,
  assignedTo = undefined,
  autoAssign = false,
) => {
  let url = `admin/users-with-instructions?limit=${limit}&offset=${offset}`;
  if (assignedTo) {
    url += `&assigned_to=${encodeURIComponent(assignedTo)}`;
  }
  if (autoAssign) {
    url += `&auto_assign=true`;
  }
  return await ApiRequest("GET", url);
};

export const GetStateCityDropdown = async () => {
  return await ApiRequest("GET", "admin/state-city-dropdown");
};

export const GetDropdownData = async () => {
  return await ApiRequest("GET", "admin/dropdown-data");
};

export const GetErrorLogs = async (filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== "") params.append(key, String(value));
  });
  return await ApiRequest("GET", `admin/error-logs/?${params.toString()}`);
};

export const GetErrorStats = async () => {
  return await ApiRequest("GET", "admin/error-logs/stats");
};

export const GetNotificationLogs = async (filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== "") params.append(key, String(value));
  });
  return await ApiRequest(
    "GET",
    `admin/notification-status-logs?${params.toString()}`,
  );
};

export const SearchSkippedMatches = async (userId, candidateId) => {
  return await ApiRequest(
    "GET",
    `admin/skipped-matches/search?user_id=${userId}&candidate_id=${candidateId}`,
  );
};

export const GetDockerStats = async () => {
  return await ApiRequest("GET", "admin/docker/stats");
};

export const GetPostgresStats = async () => {
  return await ApiRequest("GET", "admin/postgres/stats?table_limit=10");
};

export const GetCacheHealth = async () => {
  return await ApiRequestNoLoder("GET", "admin/cache/health");
};

export const GetCacheStats = async () => {
  return await ApiRequestNoLoder("GET", "admin/cache/stats");
};

export const GetCasteList = async () => {
  return await ApiRequestNoLoder("GET", "admin/caste-list");
};

// Preview gate violations before publishing a match. Returns
// {overridable:{cat:detail}, blocking:{cat:detail}, can_override:bool, needs_override:bool}
export const GetPublishPreview = async (userId, candidateId) => {
  return await ApiRequestNoLoder(
    "GET",
    `admin/match/publish-preview?user_id=${encodeURIComponent(userId)}&candidate_id=${encodeURIComponent(candidateId)}`,
  );
};

export const GetUserLikeDislikePairs = async (params = {}) => {
  const {
    filter_type = "",
    user_id = "",
    search = "",
    start_date = "",
    end_date = "",
  } = params;
  let url = "admin/v2/user-like-dislike-pairs?";
  if (filter_type) url += `&filter_type=${filter_type}`;
  if (user_id) url += `&user_id=${user_id}`;
  if (search) url += `&search=${search}`;
  if (start_date) url += `&start_date=${start_date}`;
  if (end_date) url += `&end_date=${end_date}`;
  return await ApiRequest("GET", url);
};

export const GetLLMLogs = async (params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      qs.append(key, String(value));
    }
  });
  return await ApiRequest("GET", `admin/llm/logs?${qs.toString()}`);
};

export const GetLLMLogById = async (logId) => {
  return await ApiRequest("GET", `admin/llm/logs/${logId}`);
};

export const GetLLMFilters = async (params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      qs.append(key, String(value));
    }
  });
  return await ApiRequest("GET", `admin/llm/filters?${qs.toString()}`);
};

export const GetLLMStats = async (params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      qs.append(key, String(value));
    }
  });
  return await ApiRequest("GET", `admin/llm/stats?${qs.toString()}`);
};

export const GetSammelanEventProfiles = async (
  limit = 1000,
  offset = 0,
  search = "",
) => {
  let url = `admin/sammelan-event-profiles?limit=${limit}&offset=${offset}`;
  if (search) url += `&search=${encodeURIComponent(search)}`;
  return await ApiRequest("GET", url);
};

export const GetSammelanEvents = async () => {
  return await ApiRequest("GET", "admin/sammelan-events");
};

export const GetCustomerSupportAdmins = async () => {
  return await ApiRequest("GET", "admin/customer-support-users");
};

export const GetCustomerSupportCallingDetails = async (username, timePeriod = "current") => {
  const finalUsername = username?.toLowerCase() === "soniya rathi" ? "Soniya" : username;
  return await ApiRequest(
    "GET",
    `admin/customer-support/calling-details?username=${encodeURIComponent(finalUsername)}&time_period=${timePeriod}`,
  );
};

export const GetUnregisteredUsers = async (params = {}) => {
  const {
    tag = "unregister user",
    status = "",
    limit = 50,
    offset = 0,
    assigned_to = "",
    start_date = "",
    end_date = ""
  } = params;

  let url = `admin/Details_unregister_user?tag=${encodeURIComponent(tag)}&limit=${limit}&offset=${offset}`;

  if (status) {
    url += `&status=${status}`;
  }

  const finalAssignedTo = assigned_to?.toLowerCase() === "soniya rathi" ? "Soniya" : assigned_to;
  if (finalAssignedTo) {
    url += `&assigned_to=${encodeURIComponent(finalAssignedTo)}`;
  }

  if (start_date) {
    url += `&from_date=${encodeURIComponent(start_date)}`;
  }

  if (end_date) {
    url += `&to_date=${encodeURIComponent(end_date)}`;
  }

  return await ApiRequest("GET", url);
};

export const GetUnregisteredUsersStatusStats = async (period = "all") => {
  return await ApiRequest("GET", `admin/unregistered-users-status-stats?period=${period}`);
};

export const GetActiveUsersDetails = async (assignedTo = "", limit = 1000, offset = 0, timePeriod = "") => {
  let url = `admin/active-users-details?limit=${limit}&offset=${offset}`;
  const norm = assignedTo?.toLowerCase();
  const finalAssignedTo = (norm === "soniya" || norm === "soniya rathi" || norm === "soniyarathi") ? "soniyarathi" : assignedTo;
  if (finalAssignedTo) {
    url += `&assigned_to=${encodeURIComponent(finalAssignedTo)}`;
  }
  if (timePeriod) {
    url += `&time_period=${encodeURIComponent(timePeriod)}`;
  }
  return await ApiRequest("GET", url);
};

export const GetPhoneCallEvents = async (userId, candidateId) => {
  return await ApiRequest(
    "GET",
    `admin/v2/phone-call-events/${userId}?candidate_id=${candidateId}`,
  );
};

export const GetAdminCoupons = async (params = {}) => {
  const query = new URLSearchParams();
  if (params.search) query.append("search", params.search);
  if (params.is_active !== undefined && params.is_active !== "") {
    query.append("is_active", String(params.is_active));
  }
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return await ApiRequestNoLoder("GET", `admin/coupons${suffix}`);
};

export const GetCouponRegistrations = async (params = {}) => {
  const query = new URLSearchParams();
  if (params.coupon_id) query.append("coupon_id", String(params.coupon_id));
  if (params.coupon_code) query.append("coupon_code", params.coupon_code);
  if (params.user_id) query.append("user_id", params.user_id);
  if (params.limit) query.append("limit", String(params.limit));
  if (params.offset !== undefined) query.append("offset", String(params.offset));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return await ApiRequestNoLoder(
    "GET",
    `admin/coupons/reports/registrations${suffix}`,
  );
};

export const GetCouponTransactions = async (params = {}) => {
  const query = new URLSearchParams();
  if (params.coupon_id) query.append("coupon_id", String(params.coupon_id));
  if (params.coupon_code) query.append("coupon_code", params.coupon_code);
  if (params.user_id) query.append("user_id", params.user_id);
  if (params.success_only !== undefined && params.success_only !== "") {
    query.append("success_only", String(params.success_only));
  }
  if (params.limit) query.append("limit", String(params.limit));
  if (params.offset !== undefined) query.append("offset", String(params.offset));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return await ApiRequestNoLoder(
    "GET",
    `admin/coupons/reports/transactions${suffix}`,
  );
};

// Special Preference Notes (uses X-User-Id header to identify target user)
export const GetCompatibilityCheck = async (userId, candidateId) => {
  return await ApiRequest(
    "GET",
    `admin/v2/check-service-compatibility?user_id=${encodeURIComponent(userId)}&candidate_id=${encodeURIComponent(candidateId)}`,
  );
};

export const GetSpecialPreferenceNotes = async (userId) => {
  const rawHost = localStorage.getItem("apiHost") || "http://localhost:8008/";
  const host = rawHost.endsWith("/") ? rawHost : `${rawHost}/`;
  const token = localStorage.getItem("token");
  const res = await fetch(
    `${host}profile/special-preference-notes?user_id=${userId}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : "",
        "X-User-Id": userId,
      },
    },
  );
  return await res.json();
};

export const GetCompletedUsers = async (limit = 10, offset = 0, assignedTo = "", search = "") => {
  let url = `admin/completed-users?limit=${limit}&offset=${offset}`;
  if (assignedTo) {
    url += `&assigned_to=${encodeURIComponent(assignedTo)}`;
  }
  if (search) {
    url += `&search=${encodeURIComponent(search)}`;
  }
  return await ApiRequest("GET", url);
};

export const GetHoldUsers = async (limit = 100, offset = 0) => {
  return await ApiRequest("GET", `admin/hold-users?limit=${limit}&offset=${offset}`);
};

export const SearchStates = async (search = "", limit = 10) => {
  const normalizedSearch = normalizeSearchTerm(search);
  const cacheKey = `states:${normalizedSearch}:${limit}`;
  const cached = getCachedSearchResponse(cacheKey);
  if (cached) {
    return cached;
  }

  const inFlight = searchInFlightRequests.get(cacheKey);
  if (inFlight) {
    return inFlight;
  }

  const request = ApiRequestNoLoder(
    "GET",
    `admin/search-states?search=${encodeURIComponent(normalizedSearch)}&limit=${limit}`,
  ).then((response) => {
    if (response && (response.status === true || response.status === "success")) {
      setCachedSearchResponse(cacheKey, response);
    }
    return response;
  }).finally(() => {
    searchInFlightRequests.delete(cacheKey);
  });

  searchInFlightRequests.set(cacheKey, request);
  return request;
};

export const SearchCities = async (search = "", limit = 10) => {
  const normalizedSearch = normalizeSearchTerm(search);
  const cacheKey = `cities:${normalizedSearch}:${limit}`;
  const cached = getCachedSearchResponse(cacheKey);
  if (cached) {
    return cached;
  }

  const inFlight = searchInFlightRequests.get(cacheKey);
  if (inFlight) {
    return inFlight;
  }

  const request = ApiRequestNoLoder(
    "GET",
    `admin/search-cities?search=${encodeURIComponent(normalizedSearch)}&limit=${limit}`,
  ).then((response) => {
    if (response && (response.status === true || response.status === "success")) {
      setCachedSearchResponse(cacheKey, response);
    }
    return response;
  }).finally(() => {
    searchInFlightRequests.delete(cacheKey);
  });

  searchInFlightRequests.set(cacheKey, request);
  return request;
};

export const GetMatchNotificationLog = async (userId, candidateId) => {
  return await ApiRequestNoLoder(
    "GET",
    `admin/match-notification-log?user_id=${encodeURIComponent(userId)}&candidate_id=${encodeURIComponent(candidateId)}`,
  );
};

export const GetMatchNotificationLogBatch = async (userId) => {
  return await ApiRequestNoLoder(
    "GET",
    `admin/match-notification-log-batch?userId=${encodeURIComponent(userId)}`,
  );
};

export const GetNewUsers = async (params = {}) => {
  const query = new URLSearchParams();
  if (params.from_date) query.append("from_date", params.from_date);
  if (params.to_date) query.append("to_date", params.to_date);
  if (params.user_type) query.append("user_type", params.user_type);
  if (params.limit !== undefined) query.append("limit", params.limit);
  if (params.offset !== undefined) query.append("offset", params.offset);
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return await ApiRequest("GET", `admin/new-users${suffix}`);
};

export const GetDeactivatedMatches = async (userId, candidateId = null, limit = 100, offset = 0) => {
  let url = `admin/deactivated-matches?user_id=${encodeURIComponent(userId)}&limit=${limit}&offset=${offset}`;
  if (candidateId) {
    url += `&candidate_id=${encodeURIComponent(candidateId)}`;
  }
  return await ApiRequest("GET", url);
};

export const GetNotInterestedUsers = async (limit = 100, offset = 0) => {
  return await ApiRequest("GET", `admin/v2/users/not-interested?limit=${limit}&offset=${offset}`);
};

// Search Console APIs
export const GetSearchConsoleOverview = async (period = "30d") => {
  return await fetchAdminGet(`analytics/overview?period=${period}`);
};

export const GetSearchConsoleKeywords = async (period = "30d") => {
  return await fetchAdminGet(`search-console/keywords?days=${period === "7d" ? 7 : period === "3m" ? 90 : period === "6m" ? 180 : period === "12m" ? 365 : period === "all" ? 3650 : 30}`);
};

export const GetSearchConsolePages = async (period = "30d") => {
  return await fetchAdminGet(`analytics/top-pages?period=${period}`);
};

export const GetSearchConsoleCountries = async (period = "30d") => {
  return await fetchAdminGet(`analytics/countries?period=${period}`);
};

export const GetSearchConsoleDevices = async (period = "30d") => {
  return await fetchAdminGet(`analytics/devices?period=${period}`);
};

export const GetApiLogs = async (offset = 0, limit = 100, filters = {}) => {
  let url = `admin/api-logs/?offset=${offset}&limit=${limit}`;
  if (filters.user_id) url += `&user_id=${encodeURIComponent(filters.user_id)}`;
  if (filters.user_type) url += `&user_type=${encodeURIComponent(filters.user_type)}`;
  if (filters.method) url += `&method=${encodeURIComponent(filters.method)}`;
  if (filters.endpoint) url += `&endpoint=${encodeURIComponent(filters.endpoint)}`;
  if (filters.status) url += `&status=${encodeURIComponent(filters.status)}`;
  if (filters.start_date) url += `&start_date=${encodeURIComponent(filters.start_date)}`;
  if (filters.end_date) url += `&end_date=${encodeURIComponent(filters.end_date)}`;
  return await ApiRequest("GET", url);
};

export const GetApiLogById = async (logId) => {
  return await ApiRequest("GET", `admin/api-logs/${logId}`);
};

export const GetUserMatchStats = async (userId) => {
  return await ApiRequest("GET", `admin/v2/users/${encodeURIComponent(userId)}/match-stats`);
};
export const GetWhatsappTemplates = async () => {
  return await ApiRequestNoLoder("GET", "admin/whatsapp/templates");
};

export const GetWhatsappDbTemplates = async (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      query.append(key, String(value));
    }
  });
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return await ApiRequestNoLoder("GET", `admin/whatsapp/db-templates${suffix}`);
};

export const GetWhatsappMetaTemplates = async (limit = 100) => {
  return await ApiRequestNoLoder("GET", `admin/whatsapp/meta-templates?limit=${limit}`);
};

export const GetUserDetailAudits = async (offset = 0, limit = 100, filters = {}) => {
  let url = `admin/user-detail-audits/?offset=${offset}&limit=${limit}`;
  if (filters.target_user_id) url += `&target_user_id=${encodeURIComponent(filters.target_user_id)}`;
  if (filters.actor_id) url += `&actor_id=${encodeURIComponent(filters.actor_id)}`;
  if (filters.table_name) url += `&table_name=${encodeURIComponent(filters.table_name)}`;
  if (filters.column_name) url += `&column_name=${encodeURIComponent(filters.column_name)}`;
  if (filters.date_from) url += `&date_from=${encodeURIComponent(filters.date_from)}`;
  if (filters.date_to) url += `&date_to=${encodeURIComponent(filters.date_to)}`;
  return await ApiRequest("GET", url);
};

export const GetWebUnregisteredUsers = async () => {
  return await ApiRequest("GET", "admin/web-unregistered-users");
};

export const GetAppDeviceOverview = async () => {
  return await ApiRequestNoLoder("GET", "admin/devices/overview");
};

export const GetAppDeviceBreakdown = async () => {
  return await ApiRequestNoLoder("GET", "admin/devices/breakdown");
};

export const GetAppDeviceList = async (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      query.append(key, String(value));
    }
  });
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return await ApiRequestNoLoder("GET", `admin/devices/list${suffix}`);
};

export const GetAppStoreAnalytics = async (params = {}) => {
  const query = new URLSearchParams();
  Object.keys(params).forEach((key) => {
    if (params[key] !== undefined && params[key] !== null && params[key] !== "") {
      query.append(key, params[key]);
    }
  });
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return await ApiRequestNoLoder("GET", `admin/devices/stores/summary${suffix}`);
};

export const GetGooglePlayReviews = async () => {
  return await ApiRequestNoLoder("GET", "admin/devices/google-play/reviews");
};

export const GetGooglePlaySummary = async (params = {}) => {
  const query = new URLSearchParams();
  Object.keys(params).forEach((key) => {
    if (params[key] !== undefined && params[key] !== null && params[key] !== "") {
      query.append(key, params[key]);
    }
  });
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return await ApiRequestNoLoder("GET", `admin/devices/google-play/summary${suffix}`);
};

export const GetGooglePlayOAuthUrl = async () => {
  return await ApiRequestNoLoder("GET", "admin/devices/google-play/oauth/url");
};

// Location Analytics (State & City Analytics)
export const GetLocationAnalyticsOverview = async (params = {}) => {
  const query = new URLSearchParams();
  Object.keys(params).forEach((key) => {
    if (params[key] !== undefined && params[key] !== null && params[key] !== "") {
      query.append(key, params[key]);
    }
  });
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return await fetchAdminGet(`admin/location-analytics/overview${suffix}`);
};

export const GetLocationStateAnalytics = async (params = {}) => {
  const query = new URLSearchParams();
  Object.keys(params).forEach((key) => {
    if (params[key] !== undefined && params[key] !== null && params[key] !== "") {
      query.append(key, params[key]);
    }
  });
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return await fetchAdminGet(`admin/location-analytics/states${suffix}`);
};

export const GetLocationCityAnalytics = async (params = {}) => {
  const query = new URLSearchParams();
  Object.keys(params).forEach((key) => {
    if (params[key] !== undefined && params[key] !== null && params[key] !== "") {
      query.append(key, params[key]);
    }
  });
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return await fetchAdminGet(`admin/location-analytics/cities${suffix}`);
};

export const GetLocationUsers = async (params = {}) => {
  const query = new URLSearchParams();
  Object.keys(params).forEach((key) => {
    if (params[key] !== undefined && params[key] !== null && params[key] !== "") {
      query.append(key, params[key]);
    }
  });
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return await fetchAdminGet(`admin/location-analytics/users${suffix}`);
};

